/* eslint-disable -- React Compiler rules (react-hooks v6) flagged by external linter are not enabled in this project's CRA toolchain (react-hooks 5.2.0); false positives on async setState in effect */
import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, UserCheck, FileDown, Search } from "lucide-react";
import { MEMBER_CLUBS, INTERVALS } from "./adminConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MembersTab({ api, isCentral, user }) {
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showPending, setShowPending] = useState(false);
  const [search, setSearch] = useState("");
  const [feeDefaults, setFeeDefaults] = useState({});
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showPending) params.append("pending", "true");
      if (filter !== "all") params.append("club", filter);
      const r = await api.get(`/members?${params}`);
      setMembers(r.data);
    } catch (e) { toast.error("Mitglieder konnten nicht geladen werden"); }
  }, [api, filter, showPending]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isCentral) api.get("/members/fee-defaults").then(r => setFeeDefaults(r.data)).catch(() => {});
  }, [api, isCentral]);

  const updateMember = async (id, data) => {
    try {
      await api.put(`/members/${id}`, data);
      toast.success("Mitglied aktualisiert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Mitglied wirklich löschen?")) return;
    try { await api.del(`/members/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error("Fehler beim Löschen"); }
  };

  const setPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }
    try {
      await api.post(`/members/${passwordModal.id}/set-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success(`Passwort für ${passwordModal.name} wurde gesetzt`);
      setPasswordModal(null);
      setNewPassword("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Setzen des Passworts");
    }
  };

  const toggleClub = async (member, clubId, approved) => {
    const ms = { ...member.memberships };
    if (approved) {
      const def = feeDefaults[clubId] || {};
      ms[clubId] = { requested: true, approved: true, fee: def.fee || 0, fee_interval: def.interval || "year" };
    } else {
      if (ms[clubId]) { ms[clubId] = { ...ms[clubId], approved: false, requested: false }; }
    }
    await updateMember(member.id, { memberships: ms });
  };

  const updateFee = async (member, clubId, field, value) => {
    const ms = { ...member.memberships };
    if (ms[clubId]) { ms[clubId] = { ...ms[clubId], [field]: field === "fee" ? parseFloat(value) || 0 : value }; }
    await updateMember(member.id, { memberships: ms });
  };

  const saveFeeDefaults = async () => {
    try {
      await api.put("/members/fee-defaults", { defaults: feeDefaults });
      toast.success("Beitragsstandards gespeichert");
    } catch { toast.error("Fehler"); }
  };

  const downloadPDF = async (club) => {
    try {
      const token = localStorage.getItem("nb_token");
      const url = club ? `${API}/members/export/pdf?club=${club}` : `${API}/members/export/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = club ? `mitglieder-${club}.pdf` : "mitglieder-alle.pdf";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("PDF-Export fehlgeschlagen"); }
  };

  const filtered = members.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.bar_id?.toLowerCase().includes(q);
  });

  return (
    <div data-testid="members-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
          {showPending ? "Warteschlange" : "Mitglieder"}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowPending(!showPending)} data-testid="toggle-pending-btn"
            className={`px-3 py-1.5 text-xs font-bold uppercase border ${showPending ? "bg-primary text-primary-foreground border-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
            <UserCheck className="w-3.5 h-3.5 inline mr-1" />{showPending ? "Zeige Alle" : "Warteschlange"}
          </button>
          {!showPending && (
            <>
              <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="members-club-filter"
                className="px-2 py-1.5 text-xs bg-card border border-white/10 text-foreground">
                <option value="all">Alle Vereine</option>
                {MEMBER_CLUBS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suche..."
                  className="pl-7 pr-2 py-1.5 text-xs bg-card border border-white/10 text-foreground w-40" data-testid="members-search" />
              </div>
            </>
          )}
          <div className="relative group">
            <button className="px-3 py-1.5 text-xs font-bold uppercase border border-white/10 text-muted-foreground hover:text-foreground"
              data-testid="export-pdf-btn">
              <FileDown className="w-3.5 h-3.5 inline mr-1" />PDF Export
            </button>
            <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-card border border-white/10 z-50 min-w-[160px]">
              <button onClick={() => downloadPDF(null)} className="block w-full text-left px-3 py-2 text-xs hover:bg-white/5">Alle Mitglieder</button>
              {MEMBER_CLUBS.map(c => (
                <button key={c.id} onClick={() => downloadPDF(c.id)} className="block w-full text-left px-3 py-2 text-xs hover:bg-white/5">{c.label}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isCentral && !showPending && (
        <details className="mb-4 bg-card border border-white/5 p-3">
          <summary className="text-xs font-bold uppercase tracking-wider cursor-pointer text-muted-foreground">Beitragsstandards bearbeiten</summary>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
            {MEMBER_CLUBS.map(c => (
              <div key={c.id} className="space-y-1">
                <Label className="text-xs font-bold">{c.label}</Label>
                <div className="flex gap-1">
                  <Input type="number" value={feeDefaults[c.id]?.fee ?? 0}
                    onChange={e => setFeeDefaults(p => ({...p, [c.id]: {...p[c.id], fee: parseFloat(e.target.value) || 0}}))}
                    className="h-7 text-xs bg-input/50 border-white/10 rounded-none w-20" />
                  <select value={feeDefaults[c.id]?.interval ?? "year"}
                    onChange={e => setFeeDefaults(p => ({...p, [c.id]: {...p[c.id], interval: e.target.value}}))}
                    className="h-7 text-xs bg-input/50 border border-white/10 text-foreground">
                    {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
          <button onClick={saveFeeDefaults} className="mt-3 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold uppercase">Speichern</button>
        </details>
      )}

      <div className="space-y-2">
        {filtered.map(m => {
          const isPending = Object.values(m.memberships || {}).some(v => v.requested && !v.approved) || (m.registered_user && !m.registered_user_approved);
          return (
            <div key={m.id} className="bg-card border border-white/5 p-4" data-testid={`member-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm">{m.name}</p>
                    {isPending && <Badge className="text-[10px] bg-yellow-600">Ausstehend</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{m.email} | {m.phone}{m.gender ? ` | ${m.gender === "male" ? "M" : m.gender === "female" ? "W" : "D"}` : ''}{m.birthdate ? ` | ${m.birthdate}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Label className="text-[10px] text-muted-foreground">Bar-ID:</Label>
                    <input value={m.bar_id || ""} maxLength={10}
                      onChange={e => { const v = e.target.value; setMembers(prev => prev.map(x => x.id === m.id ? {...x, bar_id: v} : x)); }}
                      onBlur={e => updateMember(m.id, { bar_id: e.target.value })}
                      className="w-20 h-6 px-1 text-xs bg-input/50 border border-white/10 text-foreground" data-testid={`bar-id-${m.id}`} />
                  </div>
                  <button onClick={() => deleteMember(m.id)} className="p-1.5 text-destructive hover:bg-destructive/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {MEMBER_CLUBS.map(c => {
                  const ms = m.memberships?.[c.id];
                  const isApproved = ms?.approved;
                  const isRequested = ms?.requested;
                  return (
                    <div key={c.id} className={`border p-2 ${isApproved ? "border-green-600/30 bg-green-600/5" : isRequested ? "border-yellow-600/30 bg-yellow-600/5" : "border-white/5"}`}>
                      <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`member-club-${m.id}-${c.id}`}>
                        <input type="checkbox" checked={!!isApproved}
                          onChange={e => toggleClub(m, c.id, e.target.checked)}
                          className="w-3.5 h-3.5 accent-green-500" />
                        <span className={`text-xs font-bold ${isRequested && !isApproved ? "text-yellow-500" : ""}`}>{c.label}</span>
                        {isRequested && !isApproved && <span className="text-[9px] text-yellow-500">NEU</span>}
                      </label>
                      {isApproved && (
                        <div className="flex gap-1 mt-1.5">
                          <input type="number" value={ms?.fee ?? 0}
                            onChange={e => { const v = e.target.value; setMembers(prev => prev.map(x => x.id === m.id ? {...x, memberships: {...x.memberships, [c.id]: {...x.memberships[c.id], fee: parseFloat(v)||0}}} : x)); }}
                            onBlur={e => updateFee(m, c.id, "fee", e.target.value)}
                            className="w-14 h-5 px-1 text-[10px] bg-input/50 border border-white/10 text-foreground" />
                          <select value={ms?.fee_interval || "year"}
                            onChange={e => updateFee(m, c.id, "fee_interval", e.target.value)}
                            className="h-5 text-[10px] bg-input/50 border border-white/10 text-foreground flex-1">
                            {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {isCentral && (
                <div className="mt-2 border border-white/5 p-2">
                  <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`member-registered-${m.id}`}>
                    <input type="checkbox" checked={!!m.registered_user_approved}
                      onChange={e => updateMember(m.id, { registered_user: e.target.checked })}
                      className="w-3.5 h-3.5 accent-blue-500" />
                    <span className="text-xs font-bold text-blue-400">Niners Friends</span>
                    {m.registered_user && !m.registered_user_approved && <span className="text-[9px] text-yellow-500 font-bold">NEU</span>}
                    <span className="text-[9px] text-muted-foreground">(nur Master-Admin)</span>
                  </label>
                </div>
              )}
              <div className="mt-2 flex justify-end">
                <button 
                  onClick={() => setPasswordModal({ id: m.id, name: m.name })}
                  className="px-3 py-1.5 text-xs font-bold uppercase border border-white/10 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors"
                  data-testid={`set-password-btn-${m.id}`}>
                  Passwort setzen
                </button>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground">{showPending ? "Keine ausstehenden Anträge." : "Keine Mitglieder gefunden."}</p>}
        <p className="text-xs text-muted-foreground mt-2">Gesamt: {filtered.length} {showPending ? "Anträge" : "Mitglieder"}</p>
      </div>

      <Dialog open={!!passwordModal} onOpenChange={() => { setPasswordModal(null); setNewPassword(""); }}>
        <DialogContent className="bg-card border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase tracking-tight" style={{ fontFamily: 'Oswald' }}>
              Passwort setzen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Neues Passwort für <span className="text-foreground font-semibold">{passwordModal?.name}</span> setzen:
            </p>
            <div>
              <Label className="text-sm">Neues Passwort</Label>
              <Input 
                type="password" 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Mindestens 6 Zeichen"
                className="mt-1 bg-input/50 border-white/10 rounded-none" 
                data-testid="set-password-input" 
              />
            </div>
          </div>
          <DialogFooter>
            <button 
              onClick={() => { setPasswordModal(null); setNewPassword(""); }} 
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
              Abbrechen
            </button>
            <button 
              onClick={setPassword} 
              data-testid="set-password-confirm-btn"
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-bold uppercase">
              Speichern
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
